const LOCAL_STORAGE_KEY = 'qt_collections_keys';
const USER_RECOVERY_KEY = 'qt_user_recovery_code';
const USER_AUTHOR_KEY = 'qt_user_author_name';

export interface SavedKeysMap {
  [collectionId: string]: string; // collectionId -> Secret Key (UUID)
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const secretKeyService = {
  /**
   * Generates a new UUID v4 secret key.
   */
  generateKey(): string {
    return crypto.randomUUID();
  },

  /**
   * Checks if a string is a valid UUID v4 format.
   */
  isValidUUID(code: string): boolean {
    return UUID_REGEX.test(code.trim());
  },

  /**
   * Gets the single User Recovery Code for this device/account (no-login).
   * Generates a new unique UUID v4 if one does not exist yet.
   */
  getUserRecoveryCode(): string {
    try {
      const stored = localStorage.getItem(USER_RECOVERY_KEY);
      if (stored && stored.trim().length > 0) {
        return stored.trim();
      }
      const newKey = this.generateKey();
      localStorage.setItem(USER_RECOVERY_KEY, newKey);
      return newKey;
    } catch (e) {
      console.error('Error reading user recovery code from localStorage', e);
      return this.generateKey();
    }
  },

  /**
   * Sets a new User Recovery Code (when restoring from QR or text code).
   */
  setUserRecoveryCode(code: string): void {
    const trimmed = code.trim();
    if (!this.isValidUUID(trimmed)) {
      throw new Error('Mã khôi phục không đúng định dạng UUID (36 ký tự).');
    }
    localStorage.setItem(USER_RECOVERY_KEY, trimmed);
  },

  /**
   * Gets the saved author display name for this user account.
   */
  getUserAuthorName(): string {
    try {
      return localStorage.getItem(USER_AUTHOR_KEY) || '';
    } catch (e) {
      console.error('Error reading user author name from localStorage', e);
      return '';
    }
  },

  /**
   * Sets and persists the user author display name.
   */
  setUserAuthorName(name: string): void {
    try {
      localStorage.setItem(USER_AUTHOR_KEY, name.trim());
    } catch (e) {
      console.error('Error saving user author name to localStorage', e);
    }
  },

  /**
   * Hashes a secret key using SHA-256 client-side.
   */
  async hashKey(secretKey: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(secretKey.trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  },

  /**
   * Gets stored secret key explicitly saved for a specific collection ID (if any).
   */
  getExplicitKeyForCollection(collectionId: string): string | null {
    const keysMap = this.getStoredKeys();
    if (keysMap[collectionId]) {
      return keysMap[collectionId];
    }
    return null;
  },

  /**
   * Checks if the device's recovery code or explicit key owns a collection.
   */
  async isCollectionOwner(collection: { id: string; secretKeyHash?: string }, userCode?: string): Promise<boolean> {
    const codeToTest = userCode || this.getUserRecoveryCode();
    const userHash = await this.hashKey(codeToTest);

    if (collection.secretKeyHash) {
      if (collection.secretKeyHash === userHash) {
        return true;
      }
      // Check legacy explicitly saved key
      const explicitKey = this.getExplicitKeyForCollection(collection.id);
      if (explicitKey) {
        const explicitHash = await this.hashKey(explicitKey);
        if (explicitHash === collection.secretKeyHash) {
          return true;
        }
      }
      return false;
    }

    // Fallback if secretKeyHash is missing
    const explicitKey = this.getExplicitKeyForCollection(collection.id);
    return explicitKey !== null;
  },

  /**
   * Gets all stored keys from legacy localStorage map if any.
   */
  getStoredKeys(): SavedKeysMap {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.error('Error reading secret keys from localStorage', e);
      return {};
    }
  },

  /**
   * Gets stored secret key for a specific collection ID ONLY if device owns it, otherwise null.
   */
  async getSecretKeyForCollection(collection: { id: string; secretKeyHash?: string }): Promise<string | null> {
    const isOwner = await this.isCollectionOwner(collection);
    if (!isOwner) return null;

    const userCode = this.getUserRecoveryCode();
    const userHash = await this.hashKey(userCode);

    if (collection.secretKeyHash && collection.secretKeyHash === userHash) {
      return userCode;
    }

    const explicitKey = this.getExplicitKeyForCollection(collection.id);
    return explicitKey || userCode;
  },

  /**
   * Saves a secret key for a collection ID to localStorage.
   */
  saveKeyForCollection(collectionId: string, secretKey: string): void {
    const keysMap = this.getStoredKeys();
    keysMap[collectionId] = secretKey.trim();
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(keysMap));
  },

  /**
   * Removes a stored secret key from localStorage map.
   */
  removeKey(collectionId: string): void {
    const keysMap = this.getStoredKeys();
    delete keysMap[collectionId];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(keysMap));
  },
};
